// ─────────────────────────────────────────────────────────────
// LukaDuka - Offline Capture Screen (ver1)
// Shown by Router when the app opens with no connection and a login is
// stored on this phone. One job, done calmly: catch the moment.
//
// ver1 - 2 November 2026 (Offline Capture screen - Layers 3 + 4):
//   Replaces the ver0 interim screen. Design brief:
//   lukaduka-offline-capture-screen-design-ver4.md, sections 4.3 and 4.4.
//
//   - "You're offline. Capture it now - we'll keep it safe until you're
//     back." Text box (150 characters), today's date, GPS when available,
//     Straight to SafeSpace switch (sealed - Decision E), Capture button.
//   - Every capture shows the same neutral "Saved safely on this phone",
//     Safespace or not. The box clears and the switch resets to off.
//   - "N moments waiting to send" - count only, no titles, sealed Safespace
//     captures never counted (Decision G). Read-only count straight from
//     the phone (countQueuedCaptures) - never asks the server.
//   - Back online (Decision H): its OWN reachability check, starting from
//     "not reachable", every 10 seconds and on the phone's 'online' event.
//     One success shows "You're back online" + Open LukaDuka. Never
//     switches automatically. Held captures send on their own regardless
//     (localCaptureQueue triggers).
//   - Always the adult account holder (Decision L): userId comes from the
//     login stored on the phone; the Account Switcher is not involved.
//   - No voice input (needs a connection). No name or email shown.
//   - GPS: enableHighAccuracy is ON here. Without a connection, Android's
//     network-based location isn't available, so the satellite fix is the
//     only way to get a location offline.
//
// Path: src/app/offline/OfflineCaptureScreen.tsx
// ─────────────────────────────────────────────────────────────

import { useCallback, useEffect, useRef, useState } from 'react'
import {
  queueOfflineCapture,
  countQueuedCaptures,
  subscribeToQueueChanges,
} from '@/modules/timeline/services/localCaptureQueue'
import { isServerReachable } from './serverReachable'
import { getLocationPreference } from '@/shared/services/locationPreference'

interface OfflineCaptureScreenProps {
  /** Account holder's ID, read from the login stored on this phone. */
  userId: string
  /** Re-runs start-up. Opens the full app when the server can be reached. */
  onOpenApp: () => void | Promise<void>
}

const CAPTURE_CHAR_LIMIT = 150
const COUNTER_WARM_AT    = 120
const SAVED_MESSAGE_MS   = 2500
const RECHECK_MS         = 10_000
const RECHECK_TIMEOUT_MS = 4_000

// Brand accents (not theme-dependent), as TimelinePage / QuickCaptureSheet.
const BRAND_ACCENT     = '#C17D4A'
const SAFESPACE_ACCENT = '#5B7FA3'

export function OfflineCaptureScreen({ userId, onOpenApp }: OfflineCaptureScreenProps) {
  const [text,         setText]         = useState('')
  const [toSafespace,  setToSafespace]  = useState(false)
  const [saving,       setSaving]       = useState(false)
  const [justSaved,    setJustSaved]    = useState(false)
  const [error,        setError]        = useState<string | null>(null)
  const [waitingCount, setWaitingCount] = useState(0)
  const [backOnline,   setBackOnline]   = useState(false)
  const [opening,      setOpening]      = useState(false)
  const [gps, setGps] = useState<{ lat: number | null; lng: number | null }>({ lat: null, lng: null })
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // ── Focus the text box ────────────────────────────────────
  useEffect(() => {
    const t = window.setTimeout(() => textareaRef.current?.focus(), 150)
    return () => window.clearTimeout(t)
  }, [])

  // ── GPS (silent, optional) ────────────────────────────────
  // Location switch (3 Nov 2026, location-privacy ver5): only when the
  // account holder's "Save location with my entries" is on. userId is the
  // account holder from the login stored on this phone (offlineIdentity.ts).
  // This screen cannot reach the server, so it relies on the phone copy -
  // no copy means off. When off, the phone is never asked, no coordinates
  // are saved and no "Location captured" shows.
  useEffect(() => {
    if (!getLocationPreference(userId)) return
    if (!navigator.geolocation) return
    navigator.geolocation.getCurrentPosition(
      (pos) => setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => { /* permission denied or no fix - continue without GPS */ },
      { enableHighAccuracy: true, timeout: 10_000, maximumAge: 120_000 }
    )
  }, [userId])

  // ── Waiting count (read-only, from the phone) ─────────────
  const refreshCount = useCallback(async () => {
    setWaitingCount(await countQueuedCaptures(userId))
  }, [userId])

  useEffect(() => {
    void refreshCount()
    const unsubscribe = subscribeToQueueChanges(() => { void refreshCount() })
    return unsubscribe
  }, [refreshCount])

  // ── Back-online check (own check, starts "not reachable") ──
  useEffect(() => {
    let cancelled = false
    let running = false

    async function check() {
      if (running || cancelled) return
      if (navigator.onLine === false) return
      running = true
      const reachable = await isServerReachable(RECHECK_TIMEOUT_MS)
      running = false
      if (!cancelled && reachable) setBackOnline(true)
    }

    void check()
    const interval = window.setInterval(() => { void check() }, RECHECK_MS)
    const onOnline = () => { void check() }
    window.addEventListener('online', onOnline)

    return () => {
      cancelled = true
      window.clearInterval(interval)
      window.removeEventListener('online', onOnline)
    }
  }, [])

  // ── "Saved" message fades after a beat ─────────────────────
  useEffect(() => {
    if (!justSaved) return
    const t = window.setTimeout(() => setJustSaved(false), SAVED_MESSAGE_MS)
    return () => window.clearTimeout(t)
  }, [justSaved])

  // ── Capture ───────────────────────────────────────────────
  async function handleCapture() {
    const trimmed = text.trim()
    if (!trimmed || saving) return

    setSaving(true)
    setError(null)
    try {
      await queueOfflineCapture(
        userId,
        {
          title: trimmed,
          captured_lat: gps.lat,
          captured_lng: gps.lng,
          captured_address: null,
          device_captured_at: new Date().toISOString(),
        },
        toSafespace
      )
      // Same words for every capture (Safespace or not), then ready again.
      setText('')
      setToSafespace(false)
      setJustSaved(true)
      void refreshCount()
      window.setTimeout(() => textareaRef.current?.focus(), 50)
    } catch (err) {
      console.error('[OfflineCaptureScreen] could not store capture:', err)
      setError("We couldn't save that on this phone. Please try again.")
    } finally {
      setSaving(false)
    }
  }

  async function handleOpenApp() {
    if (opening) return
    setOpening(true)
    try {
      await onOpenApp()
    } finally {
      // If start-up decided we're still offline, this screen stays.
      setOpening(false)
    }
  }

  const today = new Date().toLocaleDateString('en-NZ', {
    weekday: 'long', day: 'numeric', month: 'long',
  })
  const canSend = text.trim().length > 0 && !saving
  const accent  = toSafespace ? SAFESPACE_ACCENT : BRAND_ACCENT

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--ui-bg, #FAF7F2)',
      fontFamily: 'var(--ui-font-body, "Plus Jakarta Sans", sans-serif)',
      padding: '24px 20px 40px',
      boxSizing: 'border-box',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
    }}>
      <div style={{ width: '100%', maxWidth: 480 }}>

        {/* ── Back online banner (Decision H) ─────────────── */}
        {backOnline && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '12px 14px', borderRadius: 12, marginBottom: 16,
            background: `${BRAND_ACCENT}14`,
            border: `1px solid ${BRAND_ACCENT}40`,
          }}>
            <div style={{ flex: 1, fontSize: 13, fontWeight: 600, color: 'var(--ui-text, #2C2825)' }}>
              You're back online
              <div style={{ fontSize: 12, fontWeight: 400, color: 'var(--ui-text-muted, #8A8078)', marginTop: 2 }}>
                Your moments are sending now.
              </div>
            </div>
            <button
              onClick={handleOpenApp}
              disabled={opening}
              style={{
                padding: '9px 14px', borderRadius: 10, border: 'none',
                background: BRAND_ACCENT, color: '#FFFFFF',
                fontSize: 13, fontWeight: 600, cursor: opening ? 'default' : 'pointer',
                fontFamily: 'inherit', opacity: opening ? 0.7 : 1, flexShrink: 0,
              }}
            >
              {opening ? 'Opening...' : 'Open LukaDuka'}
            </button>
          </div>
        )}

        {/* ── Header ──────────────────────────────────────── */}
        <div style={{ textAlign: 'center', margin: '8px 0 20px' }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>📡</div>
          <h1 style={{
            fontSize: 20, fontWeight: 700, margin: '0 0 6px',
            color: 'var(--ui-text, #2C2825)',
          }}>
            You're offline
          </h1>
          <p style={{
            fontSize: 14, lineHeight: 1.5, margin: 0,
            color: 'var(--ui-text-muted, #8A8078)',
          }}>
            Capture it now - we'll keep it safe until you're back.
          </p>
        </div>

        {/* ── Saved message ───────────────────────────────── */}
        {justSaved && (
          <div style={{
            textAlign: 'center', fontSize: 14, fontWeight: 600,
            color: 'var(--ui-text, #2C2825)',
            padding: '10px 14px', borderRadius: 12, marginBottom: 12,
            background: 'var(--ui-card, #FFFFFF)',
            border: '1px solid var(--ui-border, #E8E2D9)',
          }}>
            📱 Saved safely on this phone
          </div>
        )}

        {/* ── Text box ────────────────────────────────────── */}
        <textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => { setText(e.target.value); if (error) setError(null) }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              void handleCapture()
            }
          }}
          placeholder={"What just happened?\n\nA few words is enough."}
          rows={4}
          maxLength={CAPTURE_CHAR_LIMIT}
          autoComplete="one-time-code"
          name="ld-offline-x1"
          style={{
            width: '100%', padding: 14, borderRadius: 12,
            border: '1.5px solid var(--ui-border, #E8E2D9)', fontSize: 15,
            fontFamily: "var(--ui-font-display, 'Lora', Georgia, serif)",
            fontStyle: 'italic',
            color: 'var(--ui-text, #2C2825)',
            background: 'var(--ui-card, #FDFBF8)',
            resize: 'none', outline: 'none',
            lineHeight: 1.6, boxSizing: 'border-box',
            marginBottom: 4,
          }}
        />
        <div style={{
          fontSize: 11, textAlign: 'right', marginBottom: 8,
          color: text.length >= COUNTER_WARM_AT ? BRAND_ACCENT : 'var(--ui-text-muted, #8A8078)',
          opacity: text.length >= COUNTER_WARM_AT ? 1 : 0.6,
        }}>
          {text.length}/{CAPTURE_CHAR_LIMIT}
        </div>

        {/* ── Error (phone could not store it) ────────────── */}
        {error && (
          <div style={{
            fontSize: 12, color: '#B83820',
            background: 'rgba(184,56,32,0.06)',
            border: '1px solid rgba(184,56,32,0.18)',
            borderRadius: 10, padding: '8px 12px', marginBottom: 10, lineHeight: 1.4,
          }}>
            {error}
          </div>
        )}

        {/* ── Date + GPS ──────────────────────────────────── */}
        <div style={{ fontSize: 12, color: 'var(--ui-text-muted, #8A8078)', margin: '4px 0 14px' }}>
          📅 {today}
          {gps.lat !== null && <span style={{ marginLeft: 8 }}>📍 Location captured</span>}
        </div>

        {/* ── Straight to SafeSpace (sealed) ──────────────── */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 14px', borderRadius: 12, marginBottom: 14,
          background: toSafespace ? `${SAFESPACE_ACCENT}14` : 'transparent',
          border: toSafespace ? `1px solid ${SAFESPACE_ACCENT}40` : '1px solid var(--ui-border, #E8E2D9)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
            <span style={{ fontSize: 16 }}>🔒</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: 13, fontWeight: 600,
                color: toSafespace ? SAFESPACE_ACCENT : 'var(--ui-text, #2C2825)',
              }}>
                Straight to SafeSpace
              </div>
              <div style={{ fontSize: 11, color: 'var(--ui-text-muted, #8A8078)', lineHeight: 1.45 }}>
                Sealed. Goes straight to your SafeSpace when you're back online.
              </div>
            </div>
          </div>
          <button
            onClick={() => setToSafespace(!toSafespace)}
            aria-label="Toggle Safespace"
            style={{
              width: 44, height: 26, borderRadius: 13, border: 'none',
              background: toSafespace ? SAFESPACE_ACCENT : 'var(--ui-border, #E8E2D9)',
              cursor: 'pointer', position: 'relative', flexShrink: 0, marginLeft: 12,
            }}
          >
            <div style={{
              width: 20, height: 20, borderRadius: 10, background: '#FFFFFF',
              position: 'absolute', top: 3, left: toSafespace ? 21 : 3,
              transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            }} />
          </button>
        </div>

        {/* ── Capture button ──────────────────────────────── */}
        <button
          onClick={() => { void handleCapture() }}
          disabled={!canSend}
          style={{
            width: '100%', padding: 14, borderRadius: 12, border: 'none',
            background: canSend ? accent : 'var(--ui-border, #E8E2D9)',
            color: '#FFFFFF', fontSize: 15, fontWeight: 600,
            fontFamily: 'inherit',
            cursor: canSend ? 'pointer' : 'not-allowed',
            opacity: canSend ? 1 : 0.5,
          }}
        >
          {saving ? 'Saving...' : 'Capture'}
        </button>

        {/* ── Waiting count (Decision G) ──────────────────── */}
        {waitingCount > 0 && (
          <p style={{
            marginTop: 16, fontSize: 12, textAlign: 'center', lineHeight: 1.5,
            color: 'var(--ui-text-muted, #8A8078)',
          }}>
            ⏳ {waitingCount} {waitingCount === 1 ? 'moment' : 'moments'} waiting to send
          </p>
        )}
      </div>
    </div>
  )
}
